import { Modal, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BeatLoader } from "react-spinners";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
import { getMood, getScheme } from "../../../../redux/selectors/colorPalette";
import {
  getSegmentColorPalette,
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { colors as Colors } from "../../../../utils";
import { getRandomId } from "../../../../utils/helpers";
import Button from "../../../Button";
import FeatureEligibility from "../../../FeatureEligibility";
import TextView from "../../../TextView";
import ColorPaletteModal from "./ColorPaletteModal";
import * as Styled from "./styles";

function transformPaletteData(data, segmentColor, previousPalette) {
  return data.map((item, i) => ({
    index: i,
    paletteName: item.palette_name,
    segmentColor,
    colors: item.colors.map((color, ind) => ({
      hex: color.hex.value,
      id: getRandomId(),
      selected: false,
      locked:
        i == previousPalette?.index
          ? previousPalette?.colors[ind]?.locked
          : false || ind === 0, // Lock the first color which is the segment color
      index: ind,
    })),
    id: getRandomId(),
    selected: i === 0,
  }));
}

export default function ColorPalette({
  title = "Color Palette",
  handleSelectColor = () => {},
  disabled,
  generateAutomatically = false,
  selectedColor,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getPaletteColor } = usePaletteColor();

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.COLOR_PALETTE);

  const selectedArc = useSelector(getSelectedSegment) || {};
  const wheelId = useSelector(getWheelId);
  const mood = useSelector(getMood);
  const scheme = useSelector(getScheme);

  const { settings: segmentSettings = {} } = selectedArc;
  const { color: segmentColor } = segmentSettings;
  const { fetchSegmentPalette } = useSegmentPalettes();

  const toggleModalVisibility = () => setIsModalVisible(!isModalVisible);

  const dispatch = useDispatch();
  const selectedPalette = useSelector(getSegmentColorPalette);
  const colors = selectedPalette ? [...selectedPalette?.colors] : [];

  useEffect(() => {
    if (
      (!selectedPalette ||
        selectedPalette?.colors[0]?.hex?.toUpperCase() !==
          segmentColor.toUpperCase()) &&
      generateAutomatically &&
      segmentColor &&
      isEligible
    )
      fetchSegmentPalette(segmentColor, selectedArc?.id);
  }, [segmentColor, isEligible]);

  if (isCheckingEligibility) {
    return (
      <Styled.Container>
        {disabled && (
          <Styled.Header>
            <TextView variant="subtitle">{title}</TextView>
          </Styled.Header>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 20,
          }}
        >
          <BeatLoader color={Colors.PrimaryPurple} size={14} />
        </div>
      </Styled.Container>
    );
  }

  if (!isEligible) {
    return (
      <Styled.Container>
        {disabled && (
          <Styled.Header>
            <TextView variant="subtitle">{title}</TextView>
          </Styled.Header>
        )}
        <FeatureEligibility
          subscription={subscription}
          requiredSubscription={requiredSubscription}
        />
      </Styled.Container>
    );
  }

  return (
    <Styled.Container>
      {!disabled && (
        <div
          style={{
            backgroundColor: Colors.lightBorder,
            padding: 8,
            borderRadius: 4,
            marginBottom: 20,
          }}
        >
          <TextView>
            *Changing the Palette will automatically update all Blocks using
            those colors!
          </TextView>
        </div>
      )}

      <Styled.Header>
        {disabled && <TextView variant="subtitle">{title}</TextView>}

        <Styled.ColorsWrapper>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <BeatLoader color={Colors.PrimaryPurple} size={14} />
            </div>
          ) : (
            !!selectedPalette && (
              <>
                <TextView
                  variant="largeText"
                  style={{
                    marginBottom: 20,
                    userSelect: "none",
                    WebkitLineClamp: 1,
                  }}
                >
                  {selectedPalette.paletteName || selectedPalette.palette_name}
                </TextView>

                <Styled.CircleRow>
                  {colors.reverse().map((color, index) => {
                    const selected = getPaletteColor(selectedColor) == color.hex;
                
                    return (
                      <Tooltip placement="top" title={color.hex}>
                        <Styled.CircleBorder
                          onClick={() =>
                            !disabled && handleSelectColor(color.index)
                          }
                          key={index}
                          index={index}
                          visible={true}
                          style={{
                            cursor: disabled ? "default" : "pointer",
                            padding: selected ? 2 : 0,
                          }}
                        >
                          <Styled.Circle
                            style={{ backgroundColor: color.hex }}
                          />
                        </Styled.CircleBorder>
                      </Tooltip>
                    );
                  })}
                </Styled.CircleRow>
              </>
            )
          )}
        </Styled.ColorsWrapper>
      </Styled.Header>

      <Button
        onClick={toggleModalVisibility}
        style={{
          width: "100%",
          marginTop: 40,
        }}
      >
        Open Segment Palette Generator
      </Button>

      {isModalVisible && (
        <Modal open={isModalVisible} onClose={() => setIsModalVisible(false)}>
          <ColorPaletteModal onClose={() => setIsModalVisible(false)} />
        </Modal>
      )}
    </Styled.Container>
  );
}
